import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Play, Pause, Volume2, VolumeX, Clock, Wind, Moon, Sun, Heart, Leaf, Waves } from 'lucide-react';

const MEDITATIONS = [
  {
    id: 'breathing',
    title: 'Lugn andning',
    description: 'En enkel andningsövning för att hitta lugn',
    duration: 5,
    icon: Wind,
    color: 'bg-sky-100 text-sky-700',
    audio: 'https://cdn.pixabay.com/audio/2022/05/27/audio_1808fbf07a.mp3', // Calm ambient
    instructions: [
      'Hitta en bekväm position',
      'Blunda och slappna av i axlarna',
      'Andas in genom näsan i 4 sekunder',
      'Håll andan i 4 sekunder',
      'Andas ut genom munnen i 6 sekunder',
      'Upprepa i din egen takt'
    ]
  },
  {
    id: 'bodyscan',
    title: 'Kroppsskanning',
    description: 'Släpp spänningar i hela kroppen',
    duration: 10,
    icon: Heart,
    color: 'bg-rose-100 text-rose-700',
    audio: 'https://cdn.pixabay.com/audio/2022/03/15/audio_115570cfd2.mp3', // Peaceful meditation
    instructions: [
      'Lägg dig bekvämt på rygg',
      'Börja med att känna dina fötter',
      'Notera eventuella spänningar',
      'Släpp dem medvetet',
      'Fortsätt uppåt genom kroppen',
      'Avsluta med att känna hela kroppen'
    ]
  },
  {
    id: 'morning',
    title: 'Morgonmeditation',
    description: 'Starta dagen med positiv energi',
    duration: 7,
    icon: Sun,
    color: 'bg-amber-100 text-amber-700',
    audio: 'https://cdn.pixabay.com/audio/2022/08/02/audio_884fe92c21.mp3', // Morning calm
    instructions: [
      'Sitt bekvämt med rak rygg',
      'Känn gratitud för en ny dag',
      'Visualisera en ljus dag framför dig',
      'Sätt en intention för dagen',
      'Andas in energi och motivation',
      'Öppna ögonen när du känner dig redo'
    ]
  },
  {
    id: 'evening',
    title: 'Kvällsmeditation',
    description: 'Avsluta dagen och förbered för sömn',
    duration: 10,
    icon: Moon,
    color: 'bg-indigo-100 text-indigo-700',
    audio: 'https://cdn.pixabay.com/audio/2021/08/04/audio_c518ab56e1.mp3', // Sleep music
    instructions: [
      'Lägg dig bekvämt i sängen',
      'Släpp dagens händelser',
      'Fokusera på din andning',
      'Låt tankarna passera som moln',
      'Känn hur kroppen blir tyngre',
      'Somna in i lugn och frid'
    ]
  },
  {
    id: 'nature',
    title: 'Naturmeditation',
    description: 'Visualisera dig i naturen',
    duration: 8,
    icon: Leaf,
    color: 'bg-emerald-100 text-emerald-700',
    audio: 'https://cdn.pixabay.com/audio/2022/01/18/audio_d0c89f5a87.mp3', // Nature sounds
    instructions: [
      'Blunda och ta några djupa andetag',
      'Föreställ dig en vacker plats i naturen',
      'Känn vinden mot huden',
      'Hör fåglarna och vattnet',
      'Känn gräset under fötterna',
      'Vila i naturens lugn'
    ]
  },
  {
    id: 'stress',
    title: 'Stresshantering',
    description: 'Snabb övning vid stressiga stunder',
    duration: 3,
    icon: Waves,
    color: 'bg-cyan-100 text-cyan-700',
    audio: 'https://cdn.pixabay.com/audio/2022/05/16/audio_460b7c3c32.mp3', // Calm waves
    instructions: [
      'Stanna upp där du är',
      'Ta 3 djupa andetag',
      'Känn fötterna mot golvet',
      'Nämn 3 saker du ser',
      'Nämn 3 saker du hör',
      'Känn hur lugnet sprider sig'
    ]
  }
];

export default function MeditationPage() {
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const [selectedMeditation, setSelectedMeditation] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = 0.5;
    }
  }, [selectedMeditation]);

  useEffect(() => {
    let interval;
    if (isPlaying && audioRef.current) {
      interval = setInterval(() => {
        const current = audioRef.current.currentTime;
        const duration = audioRef.current.duration || 1;
        setProgress((current / duration) * 100);
        setCurrentTime(current);
      }, 100);
    }
    return () => clearInterval(interval);
  }, [isPlaying]);

  const selectMeditation = (meditation) => {
    setSelectedMeditation(meditation);
    setIsPlaying(false);
    setProgress(0);
    setCurrentTime(0);
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const closeMeditation = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setSelectedMeditation(null);
    setIsPlaying(false);
    setProgress(0);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button 
            variant="ghost" 
            onClick={() => selectedMeditation ? closeMeditation() : navigate('/dashboard', { state: { expandCategory: 'mind' } })}
            className="rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {selectedMeditation ? 'Tillbaka' : 'Tillbaka'}
          </Button>
          <h1 className="font-heading text-xl">Meditationer</h1>
          <div className="w-20" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {!selectedMeditation ? (
          <>
            {/* Intro */}
            <div className="text-center space-y-3 mb-8 animate-fade-in">
              <Wind className="w-12 h-12 mx-auto text-primary" />
              <h2 className="font-heading text-3xl font-light">Hitta din inre ro</h2>
              <p className="text-muted-foreground max-w-lg mx-auto">
                Välj en meditation som passar ditt behov just nu. Sätt på dig hörlurar för bästa upplevelse.
              </p>
            </div>

            {/* Meditation cards */}
            <div className="grid gap-4 md:grid-cols-2">
              {MEDITATIONS.map((meditation, index) => {
                const Icon = meditation.icon;
                return (
                  <button
                    key={meditation.id}
                    onClick={() => selectMeditation(meditation)}
                    className="bg-card rounded-2xl p-6 text-left hover:shadow-lg transition-all animate-slide-up border border-border/50 hover:border-primary/30"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl ${meditation.color} flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading text-lg font-medium">{meditation.title}</h3>
                        <p className="text-sm text-muted-foreground mt-1">{meditation.description}</p>
                        <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          <span>{meditation.duration} min</span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          /* Active meditation view */
          <div className="animate-fade-in">
            <audio
              ref={audioRef}
              src={selectedMeditation.audio}
              loop
              onEnded={() => setIsPlaying(false)}
            />

            {/* Meditation player */}
            <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent rounded-3xl p-8 text-center space-y-6">
              <div className={`w-24 h-24 mx-auto rounded-full ${selectedMeditation.color} flex items-center justify-center`}>
                {(() => {
                  const Icon = selectedMeditation.icon;
                  return <Icon className="w-12 h-12" />;
                })()}
              </div>

              <div>
                <h2 className="font-heading text-2xl">{selectedMeditation.title}</h2>
                <p className="text-muted-foreground">{selectedMeditation.description}</p>
              </div>

              {/* Progress bar */}
              <div className="space-y-2">
                <div className="bg-secondary rounded-full h-2 overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{formatTime(currentTime)}</span>
                  <span>{selectedMeditation.duration}:00</span>
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center justify-center gap-4">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={toggleMute}
                  className="rounded-full w-12 h-12"
                >
                  {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </Button>

                <Button
                  onClick={togglePlay}
                  className="rounded-full w-20 h-20"
                  size="icon"
                >
                  {isPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8 ml-1" />}
                </Button>

                <div className="w-12 h-12" /> {/* Spacer for symmetry */}
              </div>
            </div>

            {/* Instructions */}
            <div className="mt-8 bg-card rounded-2xl p-6">
              <h3 className="font-heading text-lg mb-4">Instruktioner</h3>
              <ol className="space-y-3">
                {selectedMeditation.instructions.map((instruction, i) => (
                  <li key={i} className="flex gap-3 text-muted-foreground">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-sm flex items-center justify-center">
                      {i + 1}
                    </span>
                    <span>{instruction}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
